/**
 * FilterRKH Component
 * Provides filtering options for RKH list: date range, estate, division, and stage
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Input,
    Button,
    Select,
    Flex,
    Heading,
    FormControl,
    FormLabel,
    useTheme,
    Grid
} from '@chakra-ui/react';
import { MdClear } from 'react-icons/md';
import { FiSearch } from 'react-icons/fi';
import api from '../../../auth/apiClient';
import { RKHFilters, RKHStage, RKH_STAGE_LABELS } from '../types';

interface Estate {
    id: number;
    name: string;
    odoo_id: number;
}

interface Division {
    id: number;
    name: string;
    estate_id: number;
    odoo_id: number;
}

interface FilterRKHProps {
    filters: RKHFilters;
    onFilterChange: (filters: RKHFilters) => void;
    onClearFilters: () => void;
    onApplyFilters: () => void;
}

const FilterRKH: React.FC<FilterRKHProps> = ({
    filters,
    onFilterChange,
    onClearFilters,
    onApplyFilters,
}) => {
    const theme = useTheme();
    const [localFilters, setLocalFilters] = useState<RKHFilters>(filters);
    const [estates, setEstates] = useState<Estate[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [allDivisions, setAllDivisions] = useState<Division[]>([]);

    // Get date range defaults (Today)
    const getDefaultDateRange = () => {
        const today = new Date();

        return {
            date_from: today.toISOString().split('T')[0],
            date_to: today.toISOString().split('T')[0],
        };
    };

    const inputStyles = {
        size: "sm" as const,
        borderRadius: "lg",
        borderColor: "gray.200",
        _hover: { borderColor: "gray.300" },
        _focus: {
            borderColor: theme.colors.blue[400],
            boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
        },
    };

    // Fetch estates on component mount
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const response = await api.get('/inspeksi/options');
                const data = response.data;

                setEstates(data.estates || []);
                setAllDivisions(data.divisis || []);
            } catch (error) {
                console.error("Error fetching options:", error);
            }
        };
        fetchOptions();
    }, []);

    // Filter divisions when estate changes
    useEffect(() => {
        if (!localFilters.estate_id) {
            setDivisions([]);
            return;
        }

        // Find selected estate to get its id for filtering divisions
        const selectedEstate = estates.find(e => e.odoo_id === localFilters.estate_id);
        if (selectedEstate) {
            const filteredDivisions = allDivisions.filter(
                (div: Division) => div.estate_id === selectedEstate.id
            );
            setDivisions(filteredDivisions);
        }
    }, [localFilters.estate_id, estates, allDivisions]);

    const handleFilterUpdate = useCallback((key: keyof RKHFilters, value: any) => {
        const newFilters = { ...localFilters };

        // Handle estate change - reset division and use odoo_id
        if (key === 'estate_id') {
            newFilters.estate_id = value ? Number.parseInt(value, 10) : null;
            newFilters.division_id = null;
        } else if (key === 'division_id') {
            newFilters.division_id = value ? Number.parseInt(value, 10) : null;
        } else if (key === 'stage') {
            newFilters.stage = value ? (value as RKHStage) : null;
        } else {
            newFilters[key] = value;
        }

        setLocalFilters(newFilters);
        onFilterChange(newFilters);
    }, [localFilters, onFilterChange]);

    const handleApplyFilters = useCallback(() => {
        onFilterChange(localFilters);
        onApplyFilters();
    }, [localFilters, onFilterChange, onApplyFilters]);

    const handleClearFilters = useCallback(() => {
        const defaults = getDefaultDateRange();
        const clearedFilters: RKHFilters = {
            date_from: defaults.date_from,
            date_to: defaults.date_to,
            estate_id: null,
            division_id: null,
            stage: null,
            page: 1,
        };
        setLocalFilters(clearedFilters);
        onFilterChange(clearedFilters);
        onClearFilters();
    }, [onFilterChange, onClearFilters]);

    return (
        <Box
            display="flex"
            flexDirection="column"
            height={{ base: 'auto', md: '100%' }}
            bg="white"
            p={{ base: 4, md: 0 }}
            borderRadius={{ base: 'xl', md: 'none' }}
            boxShadow={{ base: 'sm', md: 'none' }}
            border={{ base: '1px', md: 'none' }}
            borderColor={{ base: 'gray.100', md: 'transparent' }}
        >
            {/* Header Section */}
            <Flex
                as="header"
                direction="column"
                p={4}
                display={{ base: 'none', md: 'flex' }}
                borderBottom="1px solid"
                borderColor="gray.200"
                bg="white"
            >
                <Heading size="md" mb={1} color="gray.700">
                    RKH
                </Heading>
            </Flex>

            {/* Filter Form Section */}
            <Box
                as="main"
                flex="1"
                p={{ base: 0, md: 4 }}
                overflowY={{ base: 'visible', md: 'auto' }}
                css={{
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#555',
                    },
                }}
            >
                <Grid
                    templateColumns={{
                        base: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: '1fr',
                    }}
                    gap={4}
                >
                    {/* Date From */}
                    <FormControl>
                        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                            Tanggal Mulai
                        </FormLabel>
                        <Input
                            type="date"
                            value={localFilters.date_from || ''}
                            onChange={(e) => handleFilterUpdate('date_from', e.target.value)}
                            {...inputStyles}
                        />
                    </FormControl>

                    {/* Date To */}
                    <FormControl>
                        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                            Tanggal Akhir
                        </FormLabel>
                        <Input
                            type="date"
                            value={localFilters.date_to || ''}
                            onChange={(e) => handleFilterUpdate('date_to', e.target.value)}
                            {...inputStyles}
                        />
                    </FormControl>

                    {/* Estate */}
                    <FormControl>
                        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                            Estate
                        </FormLabel>
                        <Select
                            placeholder="Pilih Estate"
                            value={localFilters.estate_id || ''}
                            onChange={(e) => handleFilterUpdate('estate_id', e.target.value)}
                            {...inputStyles}
                        >
                            {estates.map((estate) => (
                                <option key={estate.id} value={estate.odoo_id}>
                                    {estate.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Division */}
                    <FormControl>
                        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                            Divisi
                        </FormLabel>
                        <Select
                            placeholder="Pilih Divisi"
                            value={localFilters.division_id || ''}
                            onChange={(e) => handleFilterUpdate('division_id', e.target.value)}
                            isDisabled={!localFilters.estate_id || divisions.length === 0}
                            {...inputStyles}
                        >
                            {divisions.map((division) => (
                                <option key={division.id} value={division.odoo_id}>
                                    {division.name}
                                </option>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Stage */}
                    <FormControl>
                        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                            Stage
                        </FormLabel>
                        <Select
                            placeholder="Semua Stage"
                            value={localFilters.stage || ''}
                            onChange={(e) => handleFilterUpdate('stage', e.target.value)}
                            {...inputStyles}
                        >
                            {Object.entries(RKH_STAGE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Box>

            {/* Footer with Action Buttons */}
            <Flex
                as="footer"
                gap={2}
                p={{ base: 0, md: 4 }}
                mt={{ base: 4, md: 'auto' }}
                pt={{ base: 4, md: 4 }}
                borderTop={{ base: 'none', md: '1px solid' }}
                borderColor="gray.200"
                bg="white"
                direction={{ base: 'column', sm: 'row', md: 'row' }}
                justify="stretch"
            >
                <Button
                    leftIcon={<MdClear />}
                    variant="outline"
                    colorScheme="red"
                    size="sm"
                    borderRadius="lg"
                    flex={1}
                    onClick={handleClearFilters}
                >
                    Reset
                </Button>
                <Button
                    leftIcon={<FiSearch />}
                    colorScheme="blue"
                    size="sm"
                    borderRadius="lg"
                    flex={1}
                    onClick={handleApplyFilters}
                >
                    Terapkan Filter
                </Button>
            </Flex>
        </Box>
    );
};

export default FilterRKH;
